import React, { FC, useMemo } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { GetStaticPaths } from 'next';
import Head from 'next/head';
import Image from "next/legacy/image";
import OutlinedPin from 'components/Icons/OutlinedPin';
import StoreCard from 'components/StoreCard/StoreCard';
import { CALL_CENTER_PHONE_NUMBER_BY_REGION } from 'constants/callCenter';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import { stringToSlug } from 'functions/slug'
import notionStoreDataService from 'services/notion.stores.service';
import Link from 'next/link';
import { getLocaleUrl } from 'functions/getLocaleUrl';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { SeoBlock, SeoMetaTags } from 'constants/dataTypes';
import { getGoogleReviews, getSeoBlockContent, getSeoMetaTags } from 'utils/common.utils';
import SeoText from 'components/Others/SeoText';
import storeSeoContent from 'data/store-seo';
import { useRouter } from 'next/router';
import CategorySeoText from 'components/Others/CategorySeoText';
import StoreHeader from 'components/StoreHeader/StoreHeader';
import Direction from 'components/Icons/Direction';
import ChatBox from 'components/Icons/ChatBox';
import ServiceIcon from 'components/Icons/Services';
import GooglePageReviews from 'components/GoogleReview/GooglePageReviews';
import cx from 'classnames';
import ContactUsX from 'components/Icons/ContactUsX';
import ContactUsEmail from 'components/Icons/ContactUsEmail';
import ContactUsFacebook from 'components/Icons/ContactUsFacebook';
import ContactUsInstagram from 'components/Icons/ContactUsInstagram';
import ContactUsLinkedin from 'components/Icons/ContactUsLinkedin';
import ContactUsUsers from 'components/Icons/ContactUsUsers';
import StoreGooglePageReviews from 'components/GoogleReview/StoreGoogleReview';


const regions: any = {
  "au": {
    "email": "hello@dresden.vision",
    "phone": "(02) 5300 3003",
    "chat": "Zendesk Chat for AU",
    "workingHours": "09:00 - 17:30 AEDT",
    "socialMedia": [
      { "platform": "Instagram", "url": "https://www.instagram.com/dresdenvision/", "description": "Instagram (@dresdenvision)" },
      { "platform": "Facebook", "url": "https://www.facebook.com/dresdenvision", "description": "Facebook" },
      { "platform": "X", "url": "https://twitter.com/dresdenvision/", "description": "Dresden Vision (@dresdenvision) on X" },
      { "platform": "Linkedin", "url": "https://www.linkedin.com/company/dresdenvision/", "description": "Linkedin" }
    ],
    "careerUrl": "https://dresden.vision/au/career",
    "mediaEmail": "bonnie@au.dresden.vision"
  },
  "nz": {
    "email": "hello@dresden.vision",
    "phone": "0800 447 111",
    "chat": "Zendesk Chat for NZ",
    "workingHours": "11:00 - 19:00  NZST",
    "socialMedia": [
      { "platform": "Instagram", "url": "https://www.instagram.com/dresdenvision/", "description": "Instagram (@dresdenvision)" },
      { "platform": "Facebook", "url": "https://www.facebook.com/dresdenvision", "description": "Facebook" },
      { "platform": "X", "url": "https://twitter.com/dresdenvision/", "description": "Dresden Vision (@dresdenvision) on X" },
      { "platform": "Linkedin", "url": "https://www.linkedin.com/company/dresdenvision/", "description": "Linkedin" }
    ],
    "mediaEmail": "bonnie@au.dresden.vision"
  },
  "ca": {
    "email": "hello@ca.dresden.vision",
    "phone": "(647) 424 4288",
    "chat": "Zendesk Chat for CA",
    "workingHours": "09:00 - 18:00 EST. Saturday 11:00 - 18:00 EST.",
    "socialMedia": [
      { "platform": "Instagram", "url": "https://www.instagram.com/dresdenvisionca/", "description": "Instagram (@dresdenvision)" },
      { "platform": "Facebook", "url": "https://www.facebook.com/dresdenvisioncanada", "description": "Facebook" },
      { "platform": "X", "url": "https://twitter.com/dresdenvision/", "description": "Dresden Vision (@dresdenvision) on X" },
      { "platform": "Linkedin", "url": "https://www.linkedin.com/company/dresdenvision/", "description": "Linkedin" }
    ],
    "mediaEmail": "mike@ca.dresden.vision"
  },
  "international": {
    "email": "hello@dresden.vision",
    "phone": "+1 (213) 223-6100",
    "chat": "Zendesk Chat for AU",
    "workingHours": "09:00 - 17:30 AEDT",
    "socialMedia": [
      { "platform": "Instagram", "url": "https://www.instagram.com/dresdenvision/", "description": "Instagram (@dresdenvision)" },
      { "platform": "Facebook", "url": "https://www.facebook.com/dresdenvision", "description": "Facebook" },
      { "platform": "X", "url": "https://twitter.com/dresdenvision/", "description": "Dresden Vision (@dresdenvision) on X" },
      { "platform": "Linkedin", "url": "https://www.linkedin.com/company/dresdenvision/", "description": "Linkedin" }
    ],
    "mediaEmail": "bonnie@au.dresden.vision"
  }
}



function createDateInTimezone(dateString: string | number | Date, timeZone: string) {
  try {
    if (typeof dateString === 'string' && dateString.length === 10) {
      dateString = `${dateString}T00:00:00.000Z`;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error(`Invalid date: ${dateString}`);
      return null;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const dateObject: Record<string, string> = {};
    parts.forEach(part => {
      dateObject[part.type] = part.value;
    });

    const month = dateObject.month.padStart(2, '0');
    const day = dateObject.day.padStart(2, '0');
    const hour = dateObject.hour.padStart(2, '0');
    const minute = dateObject.minute.padStart(2, '0');
    const second = dateObject.second.padStart(2, '0');

    const offset = timeZone === 'America/Toronto' ? '-05:00' : '+11:00';
    return `${dateObject.year}-${month}-${day}T${hour}:${minute}:${second}.000${offset}`;
  } catch (error) {
    console.error(`Error formatting date: ${dateString}`, error);
    return null;
  }
}

function getWeekDates(timeZone: string) {
  const today = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone,
    weekday: 'long',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const dayName = [];
  const dayDate = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(today.getDate() + i);

    const formattedDate = formatter.format(date);
    const [weekday, monthDayYear] = formattedDate.split(', ');
    const [month, day, year] = monthDayYear.split('/');

    dayName.push(weekday);
    dayDate.push(`${year}-${month}-${day}`);
  }

  return { dayName, dayDate };
}

type StoreHolidayDate = {
  start: string;
  end?: string;
};
type StoreHoliday = {
  date: StoreHolidayDate;
  openingHours: string;
  name: string;
};
type StoreDetailProps = {
  store: any;
  smilarStores: [];
  storeHoliday: StoreHoliday[];
  timeZone: string;
  seoMetaTags: SeoMetaTags;
  seoBlock: SeoBlock;
  reviews: any;
  similarStores: any;
};

export default function StoreDetail({
  store,
  smilarStores,
  storeHoliday,
  timeZone,
  seoMetaTags,
  seoBlock,
  reviews,
  similarStores
}: StoreDetailProps) {
  const router = useRouter();
  const callCenterContactNumber = useMemo(() => {
    const countryCodeMap: Record<string, string> = {
      Australian: 'au',
      Canadian: 'ca',
      canada: 'ca',
      australia: 'au'
    };
    return (
      CALL_CENTER_PHONE_NUMBER_BY_REGION[countryCodeMap[store.country]] ||
      CALL_CENTER_PHONE_NUMBER_BY_REGION.international
    );
  }, [store.country]);
  const currentRegion = regions[router.locale as any];

  const data = useMemo(
    () => {
      // Handle Canadian stores differently
      if (store.country === 'Canadian' || store.country === 'canada') {
        return [
          {
            id: 1,
            href: '',
            text: 'Dresden Vision'
          },
          {
            id: 2,
            href: 'stores',
            text: 'Stores'
          },
          {
            id: 4,
            href: 'stores/' + stringToSlug(store.city),
            text: store.city // This will be Toronto or Hamilton
          },
          {
            id: 5,
            href: '',
            text: store.name // This will be Little Italy, etc.
          }
        ];
      }

      // Default structure for other regions (AU, etc.)
      return [
        {
          id: 1,
          href: '',
          text: 'Dresden Vision'
        },
        {
          id: 2,
          href: 'stores',
          text: 'Stores'
        },
        {
          id: 3,
          href: 'stores/' + stringToSlug(store.state),
          text: store.state == 'Hamilton City' ? 'Hamilton' : store.state
        },
        {
          id: 4,
          href: '',
          text: store && store.name
        }
      ];
    },
    [store]
  );

  const { dayName, dayDate } = getWeekDates(timeZone);
  //phoneNumberString can be (02) 5300 3003 for au, 416 461 2221 for ca. return in format +61253003003, +14164612221
  function formatPhoneNumberToInt(phoneNumberString: string | null | undefined) {
    if (!phoneNumberString) return '';

    const countryCodeMap: Record<string, string> = {
      Australian: '+61',
      Canadian: '+1',
      canada: '+1',
      australia: '+61'
    };
    const countryCode = countryCodeMap[store.country] || '+61';
    return countryCode + phoneNumberString.replace(/\D/g, '');
  }
  //console.log('store', store);
  /*
  const holidays = storeWeek.filter((day) => {
    //day has format 'Sunday, 2023-12-03', so we need to remove 'Sunday, ' to compare with holiday date
    day = day.substring(day.indexOf(',') + 2);
    return storeHoliday.some((holiday) => {
      //compare only year, month and day and day is between holiday start and end date by Date.parse and dont use substr to get partial string
      return Date.parse(holiday.date.start) <= Date.parse(day) && (!holiday.date.end || Date.parse(day) <= Date.parse(holiday.date.end));
    });
  });
  
  console.log('storeHoliday', storeWeek);*/

  const storeContent: any = storeSeoContent[store.slug];

  const storeSeoText = useMemo(() => {
    if (!storeContent) return null;
    let storeContentText = storeContent.seoContent;
    if (storeContentText) {
      if (router.locale == 'ca') {
        storeContentText = storeContentText.replaceAll("https://dresden.vision/au", "https://dresden.vision/ca");
      } else if (router.locale == 'international') {
        storeContentText = storeContentText.replaceAll("https://dresden.vision/au", "https://dresden.vision");
      } else if (router.locale == 'nz') {
        storeContentText = storeContentText.replaceAll("https://dresden.vision/au", "https://dresden.vision/nz");
      }
    }
    return storeContentText;
  }, [storeContent]);

  const getSocialMedia = (platform: string) => {
    const platformData = currentRegion.socialMedia.find((social: any) => social.platform === platform);
    if (platformData) {
      return platformData.url;
    }
  }

  const isFitzroy = store.name.toLowerCase().includes('fitzroy');

  return (
    <div>
      <Head>
        <title>{`${store && store.name} | Dresden Vision`}</title>
        <meta name="twitter:title" content={`${store && store.name}`} />
        <meta name="og:title" content={`${store && store.name}`} />
        <meta
          name="description"
          content={`${store && store.name
            } - Dresden Vision address, phone number, e-mail address and appointment information are available on this page.`}
        />
        <meta
          property="og:description"
          content={`${store && store.name
            } - Dresden Vision address, phone number, e-mail address and appointment information are available on this page.`}
        />
        <meta
          name="twitter:description"
          content={`${store && store.name
            } - Dresden Vision address, phone number, e-mail address and appointment information are available on this page.`}
        />
      </Head>

      <StoreHeader
        storeName={store.name}
        storeImage={store.featured_image_url}
        country={store.country}
      />

      <div className="px-4 pt-6 mdx:px-0 mdx:pt-0">
        <div className="mx-auto max-w-[1224px] mt-0 mdx:mt-2">
          <Breadcrumb data={data} />
        </div>

        {!store?.isActive ? (
          <div>
            <PassiveStoreBanner
              name={store.name}
              contactNumber={callCenterContactNumber}
            />
            <span className="mb-[16px] mt-[32px] block text-[24px] font-bold text-brand-black100">
              Stores near {store.name}
            </span>
            <SmilarStoreList stores={smilarStores} />
          </div>
        ) : (
          <>
            <div className='mdx:mt-[0px] mt-[40px] bg-[#F8F8F8]'>
              <div className="max-w-[1224px] mx-auto">
                {/* Three Column Layout Container */}
                <div className="grid grid-cols-12 gap-8">
                  {/* Map Section - Left Column (5 columns) */}
                  <div className="mdx:hidden block col-span-12 lg:col-span-5 min-h-[580px] w-full">
                    <LoadScript googleMapsApiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string}>
                      <GoogleMap
                        mapContainerStyle={{
                          width: '100%',
                          height: '100%',
                        }}
                        center={{
                          lat: store.location.lat,
                          lng: store.location.lng
                        }}
                        zoom={14}>
                        <Marker
                          position={{
                            lat: store.location.lat,
                            lng: store.location.lng
                          }}
                        />
                      </GoogleMap>
                    </LoadScript>
                  </div>

                  {/* Store Info Section - Middle Column (3 columns) */}
                  <div className="col-span-12 lg:col-span-3 space-y-8 py-8 mdx:px-4 px-0">
                    {/* Visit Section */}
                    <div>
                      <div className='flex items-center justify-start gap-2'>
                        <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Visit Our Store</h2>
                        {isFitzroy && (
                          <button className='text-[14px] font-semibold bg-[#00BE82] text-white px-[12px] py-[4px] rounded-[6px]'>New Location</button>
                        )}
                      </div>
                      <p className="mt-2 text-[16px] text-[#959595]">{store.address}</p>
                      <Link
                        href={`${store.google_location_link}`}
                        className="mt-3 inline-flex items-center text-[#00AEC7] hover:text-brand-orange transition-colors"
                        target="_blank"
                      >
                        Get Directions
                        <Direction className='ml-2' fill1='#80D6E3' />
                      </Link>
                    </div>



                    {/* Call Section */}
                    <div>
                      <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Call Us</h2>
                      <Link
                        href={`tel:${formatPhoneNumberToInt(store.phone)}`}
                        className="mt-2 inline-block text-[16px] text-[#959595] hover:text-brand-orange transition-colors"
                      >
                        {store.phone}
                      </Link>
                    </div>

                    {/* Email Section */}
                    <div>
                      <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Email</h2>
                      <Link
                        href={`mailto:${store.email}`}
                        className="mt-2 inline-block text-[16px] text-[#959595] hover:text-brand-orange transition-colors"
                      >
                        {store.email}
                      </Link>
                    </div>

                    {/* Online Chat Section */}
                    <div>
                      <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Online Chat</h2>
                      <button className="mt-2 inline-flex items-center text-brand-blue hover:text-brand-orange transition-colors" onClick={() => {
                        try {
                          if (window) {
                            //@ts-ignore
                            window.zE("messenger", "open")
                          }
                        } catch (e) {
                          console.log("zendesk not loaded")
                        }
                      }}>
                        <ChatBox className="mr-2" />
                        Start Chat
                      </button>
                    </div>
                  </div>

                  {/* Right Column - Opening Hours and Parking (4 columns) */}
                  <div className="col-span-12 lg:col-span-4 space-y-8 py-8 mdx:px-4 px-0">
                    {/* Opening Hours Section */}
                    <div className='bg-white rounded-[20px] p-6'>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="bg-[#F8F8F8] p-3 rounded-full shrink-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Opening Hours</h2>
                      </div>

                      <div className="space-y-3">
                        {dayName.map((day: string, index) => {
                          const currentDate = createDateInTimezone(dayDate[index], timeZone);
                          if (currentDate) {
                            const holidayDay = { name: '', openingHours: '' };
                            storeHoliday.forEach((holiday: any) => {
                              if ((Date.parse(holiday.date.start) == Date.parse(currentDate) && !holiday.date.end) ||
                                (Date.parse(holiday.date.start) <= Date.parse(currentDate) && Date.parse(currentDate) <= Date.parse(holiday.date.end))) {
                                holidayDay.name = holiday.name;
                                holidayDay.openingHours = holiday.openingHours;
                              }
                            });

                            let openingHours = store.openDays[day.toLowerCase()];
                            if (holidayDay && holidayDay.name) {
                              openingHours = holidayDay.name + ' - ' + holidayDay.openingHours;
                            }
                            return (
                              <div key={day} className="flex justify-between items-center">
                                <span className="text-[14px] text-[#959595]">{day}</span>
                                <span className="text-[14px] text-[#959595] text-right">{openingHours}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>

                    {/* Parking Spot Section */}

                    {store.parking_info && (
                      <div className='bg-white rounded-[20px] p-6'>
                        <div className="flex items-center gap-4 mb-6">
                          <div className="bg-[#F8F8F8] p-3 rounded-full w-[40px] h-[40px] flex items-center justify-center shrink-0">
                            <svg width="13" height="17" viewBox="0 0 13 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3.8999 11.001V15.301C3.8999 16.1294 3.22833 16.801 2.3999 16.801C1.57148 16.801 0.899902 16.1294 0.899902 15.301V2.00098C0.899902 0.896408 1.79533 0.000976562 2.8999 0.000976562H7.3999C8.91907 0.000976562 10.2156 0.53781 11.2894 1.61148C12.3631 2.68531 12.8999 3.98181 12.8999 5.50098C12.8999 7.02014 12.3631 8.31664 11.2894 9.39048C10.2156 10.4641 8.91907 11.001 7.3999 11.001H3.8999ZM3.8999 8.00098H7.5229C8.20757 8.00098 8.7954 7.75548 9.2864 7.26448C9.7774 6.77348 10.0229 6.18564 10.0229 5.50098C10.0229 4.81631 9.7774 4.22848 9.2864 3.73748C8.7954 3.24648 8.20757 3.00098 7.5229 3.00098H3.8999V8.00098Z" fill="#2A2A2A" />
                            </svg>
                          </div>
                          <h2 className="text-[14px] font-semibold text-[#2A2A2A]">Parking Options</h2>
                        </div>

                        <div className="space-y-2">
                          <div
                            className="text-[14px] text-[#959595] leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: store.parking_info }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mx-auto max-w-[1224px] mdx:px-4 px-0">
              <h2 className="mt-8 text-[32px] font-normal text-center ">
                Services available in
              </h2>
              <h2 className="text-[32px] font-normal text-center mb-4">
                the <span className="text-brand-black100 font-semibold">{store.name}</span> store
              </h2>
              <p className="text-center text-[#2A2A2A] text-[16px] mb-12">
                Top-Quality Eye Care & Stylish Glasses in {store.city}, {store.state} – Your Local Optometry Experts, Shop Online or Visit In-Store
              </p>

              <div className="grid grid-cols-5 gap-8 lgx:grid-cols-2 mdx:grid-cols-2">
                {store && store.services.map((service: any, index: number) => {
                  return (
                    <div
                      key={index}
                      className="bg-[#FAFAFA] rounded-[20px] mdx:px-4 mdx:py-6  px-8 py-6 flex flex-col items-center text-center justify-center"
                    >
                      <div className="mb-6">
                        <ServiceIcon name={service} />
                      </div>
                      <h3 className="mdx:text-[13px] text-[16px] font-medium text-[#2A2A2A]">
                        {service}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <StoreGooglePageReviews reviews={reviews} storeInfo={store} />
            </div>

            {(similarStores && router.locale !== 'international' && router.locale !== 'nz') && (
              <div className=" mx-auto max-w-[1224px] mdx:px-4 px-0">
                <h2 className="text-[32px] text-center font-normal">
                  Our Other Stores
                </h2>
                <h2 className="text-[32px] text-center font-normal mb-12">
                  in <span className="font-bold">{
                    ({
                      6: 'Australia',
                      5: 'Canada',
                      7: 'New Zealand'
                    } as Record<number, string>)[store.region_id] || 'Australia'
                  }</span>
                </h2>

                <div className="grid lg:grid-cols-5 md:grid-cols-3 grid-cols-1 gap-6">
                  {similarStores.sort((a: any, b: any) => a.name.localeCompare(b.name)).map((store: any, index: any) => (
                    <div
                      key={index}
                      className="bg-white border border-[#EAEAEA] rounded-lg p-6 hover:shadow-lg transition-shadow"
                    >
                      <h3 className="text-[18px] font-semibold text-[#2A2A2A] mb-2">
                        {store.name}
                      </h3>
                      <div className="text-[16px] text-[#959595] mb-4">
                        {store.main_address}
                      </div>
                      <Link
                        href={`/stores/${stringToSlug(store.name)}`}
                        className="inline-flex items-center text-[#00AEC7] hover:text-brand-orange transition-colors text-[14px] flex justify-center"
                      >
                        Visit Store
                        <svg className="ml-2 mt-1" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              className={cx(
                'grid grid-cols-1 gap-4  mx-auto max-w-[1224px] mt-[60px] mdx:px-4 px-0',
                {
                  'md:grid-cols-3': router.locale == 'ca'
                },
                {
                  'md:grid-cols-2': router.locale !== 'ca'
                },
              )}>
              <div className='bg-[#F6F6F6] h-[185px] rounded-[12px] p-[30px]'>
                <div>
                  <p className='text-2xl text-[#2A2A2A]'>Social Media</p>
                  <p className='text-[#959595] text-sm'>Find us on social media</p>
                </div>

                <div className='mt-[30px] flex flex-row gap-[14px]'>
                  <Link href={getSocialMedia('Facebook')} target='_blank'>
                    <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                      <ContactUsFacebook />
                    </div>
                  </Link>
                  <Link href={getSocialMedia('X')} target='_blank'>
                    <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                      <ContactUsX />
                    </div>
                  </Link>

                  <Link href={getSocialMedia('Instagram')} target='_blank'>
                    <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                      <ContactUsInstagram />
                    </div>
                  </Link>

                  <Link href={getSocialMedia('Linkedin')} target='_blank' >
                    <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                      <ContactUsLinkedin />
                    </div>
                  </Link>
                </div>
              </div>
              {(router.locale == 'ca') && (
                <div className='bg-[#F6F6F6] h-[185px] rounded-[12px] p-[30px]'>
                  <div>
                    <p className='text-2xl text-[#2A2A2A]'>Come Work With Us!</p>
                    <p className='text-[#959595] text-sm'>Join us on our mission to make glasses simple</p>
                  </div>

                  <div className='mt-[30px] flex flex-row gap-[14px]'>
                    <Link href="/career">
                      <div className='flex items-center justify-center gap-[10px] '>
                        <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                          <ContactUsUsers />
                        </div>
                        <p className='text-lg text-[#2A2A2A]'>Open positions</p>
                      </div>
                    </Link>

                  </div>
                </div>
              )}

              <div className='bg-[#F6F6F6] h-[185px] rounded-[12px] p-[30px]'>
                <div>
                  <p className='text-2xl text-[#2A2A2A]'>Media Enquiries</p>
                  <p className='text-[#959595] text-sm'>Drop us a line for all media enquiries</p>
                </div>

                <div className='mt-[30px] flex flex-row gap-[14px]'>
                  <div className='flex items-center justify-center gap-[10px] '>
                    <div className='bg-white h-[48px] w-[48px] flex items-center justify-center	rounded-full'>
                      <ContactUsEmail />
                    </div>
                    <p className='text-lg text-[#2A2A2A]'>{currentRegion.mediaEmail}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}


        <div className='mx-auto max-w-[1224px] mt-[60px] mdx:px-4 px-0'>

          <h2 className='text-[32px] font-normal text-center'>Learn more about <br></br> our <span className='font-bold'>{store.name}</span> store</h2>
          {seoBlock && seoBlock.text != "" ? (
            <>
              <div className='mt-8 mb-8'>
                <CategorySeoText seoBlock={seoBlock} />
              </div>
            </>
          ) : (
            <>
              <div className='mt-8 mb-8'>
                <SeoText content={storeSeoText} />
              </div>
            </>
          )}

        </div>

      </div >
      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </div >
  );
}

const PassiveStoreBanner: FC<{ contactNumber: string; name: string }> = ({
  contactNumber,
  name
}) => {
  return (
    <div className="bg-brand-blue50 p-[26px] md:p-[38px]">
      <div className="flex flex-col-reverse sm:flex-row">
        <div className="flex flex-auto flex-col justify-center">
          <span className="mb-[12px] text-[24px] font-medium text-brand-black100 sm:text-[32px]">
            We have moved{' '}
            <strong className="block sm:inline-block">{name}</strong> store!
          </span>
          <span className="text-[14px] text-brand-grey25 sm:text-[16px]">
            If you have any question, just give us a bell on{' '}
            <a
              href={`tel:${contactNumber.replace(/\D/g, '')}`}
              className="text-medium hover:text-brand-orange">
              {contactNumber}
            </a>
            .
          </span>
        </div>
        <div className="mb-[10px] flex-shrink-0 text-brand-blue25 sm:mb-0">
          <OutlinedPin
            className="h-auto w-[48px] md:w-auto"
            fill="currentColor"
          />
        </div>
      </div>
    </div>
  );
};

const SmilarStoreList: FC<{ stores: any[] }> = ({ stores }) => {
  return (
    <div className="grid grid-cols-1 gap-[24px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      {stores.map((store) => (
        <StoreCard key={store.id} data={store} />
      ))}
    </div>
  );
};

// Update Store type to match actual API response
type Store = {
  id: number;
  region_id: number;
  name: string;
  state: string;
  city: string;
  main_address: string;
  latitude: number;
  longitude: number;
  is_active: boolean;
  booking_enable: number;
  hide_in_ecommerce: number;
  email: string;
  phone: string;
  featured_image: string | null;
  services: Array<{ service_name: string }>;
  default_working_hours: Array<{ day: string; hours: string }>;
  outlet_holidays: any[];
  regional_holidays: any[];
  timezone: string;
  default_providers: Array<{ providerNo: string; providerName: string }> | null;
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const res = await fetch(process.env.NEXT_PUBLIC_LARAVEL_URL + '/old-api/store/list');
  const stores: Store[] = await res.json();
  const paths: any[] = [];
  const availableStores = stores.filter(store => store.is_active && !store.hide_in_ecommerce);

  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  availableStores.forEach((store) => {
    if (locales) {
      for (const locale of locales) {
        paths.push({
          params: {
            storeslug: store.name.toLowerCase().replace(/\s+/g, '-')
          },
          locale
        });
      }
    }
  });

  return {
    paths,
    fallback: 'blocking'
  };
};

export const getStaticProps = async ({ params, locale }: { params: any; locale: string; }) => {
  const res = await fetch(process.env.NEXT_PUBLIC_LARAVEL_URL + '/old-api/store/list');
  const stores: Store[] = await res.json();

  const storeInfo = stores.find(store =>
    store.name.toLowerCase().replace(/\s+/g, '-') === params.storeslug
  );

  if (!storeInfo) {
    return {
      redirect: {
        destination: getLocaleUrl('/stores', locale),
        permanent: false
      }
    };
  }

  // Get similar stores - same state/region, active, and visible in ecommerce
  const similarStores = stores.filter(store =>
    store.region_id === storeInfo.region_id &&
    store.is_active &&
    !store.hide_in_ecommerce &&
    store.id !== storeInfo.id &&
    store.name.toLowerCase() !== 'replenish'
  ).slice(0, 5);
  // Format holidays from regional_holidays and outlet_holidays
  const allHolidays = [
    ...(storeInfo.outlet_holidays || []).map(holiday => ({
      ...holiday,
      type: 'outlet'
    })),
    ...(storeInfo.regional_holidays || []).map(holiday => ({
      ...holiday,
      type: 'regional'
    }))
  ];

  const storeHoliday = allHolidays.map(holiday => ({
    date: {
      start: new Date(holiday.date_from).toISOString(),
      end: holiday.date_to ? new Date(holiday.date_to).toISOString() : undefined
    },
    openingHours: holiday.working_hours,
    name: holiday.type === 'outlet' ? 'Store Holiday' : 'Regional Holiday'
  }));

  // Convert default_working_hours to openDays format
  const openDays: Record<string, string> = {};
  storeInfo.default_working_hours?.forEach(({ day, hours }) => {
    openDays[day.toLowerCase()] = hours;
  });

  // Format store data for the template
  const formattedStore = {
    ...storeInfo,
    slug: params.storeslug,
    address: storeInfo.main_address,
    location: {
      lat: storeInfo.latitude,
      lng: storeInfo.longitude
    },
    featureImage: storeInfo.featured_image
      ? `${process.env.NEXT_PUBLIC_ASSET_URL}/${storeInfo.featured_image}`
      : null,
    services: storeInfo.services?.map(s => s.service_name) || [],
    optometrists: storeInfo.default_providers?.map(provider => ({
      id: provider.providerNo,
      name: provider.providerName
    })) || [],
    country: storeInfo.region_id === 6 ? 'Australian' : 'Canadian',
    openDays,
    isActive: storeInfo.is_active && !storeInfo.hide_in_ecommerce,
    bookingEnable: Boolean(storeInfo.booking_enable)
  };

  const seoMetaTags = await getSeoMetaTags('/stores/' + params.storeslug, locale);

  const seoBlock = await getSeoBlockContent(params.storeslug, undefined);

  const reviewsRes = await fetch(process.env.NEXT_PUBLIC_LARAVEL_URL + '/old-api/outlet/reviews?outlet_id=' + storeInfo.id);
  const reviews: any = await reviewsRes.json();

  return {
    props: {
      store: formattedStore,
      similarStores: similarStores,
      storeHoliday,
      timeZone: storeInfo.timezone ||
        (storeInfo.region_id === 6 ? 'Australia/Sydney' : 'America/Toronto'),
      seoMetaTags,
      seoBlock,
      reviews
    }
  };
};


